// Encodes/decodes WebRTC SignalingPayload to short URL-safe strings.
//
// Format:  v1.<kind-letter>.<lzbase64url-of-json>
//   v1     — codec version (rejects garbage / future formats explicitly)
//   kind   — 'o' for offer, 'a' for answer (sanity check before parse)
//   body   — lz-string compressed JSON, base64url-encoded for URL/clipboard
//
// Compression: SDP is highly repetitive (m= a=ice-* a=candidate, etc.); lz-string
// typically shrinks a STUN-only offer with bundled ICE from ~2.4 KB to ~900 B.
// Browsers accept URLs up to ~32 KB; ~2 KB fragment is the conservative ceiling
// we surface to the UI for the "copy this blob" fallback.

import { compressToEncodedURIComponent, decompressFromEncodedURIComponent } from 'lz-string';
import type { SignalingPayload } from './types';

const CODEC_VERSION = 'v1';
const KIND_LETTER: Record<SignalingPayload['kind'], string> = { offer: 'o', answer: 'a' };
const LETTER_KIND: Record<string, SignalingPayload['kind']> = { o: 'offer', a: 'answer' };

// Soft URL-fragment cap. Real browsers go higher, but Slack / iMessage and
// some chat apps truncate around 2 KB. Anything above we surface to the UI so
// the host can fall back to the "paste this blob" panel.
export const SOFT_LENGTH_LIMIT = 1800;

export class SignalingCodecError extends Error {
  constructor(reason: string) {
    super(`signalingCodec: ${reason}`);
    this.name = 'SignalingCodecError';
  }
}

export function encodeSignaling(payload: SignalingPayload): string {
  const letter = KIND_LETTER[payload.kind];
  const json = JSON.stringify(payload);
  const body = compressToEncodedURIComponent(json);
  return `${CODEC_VERSION}.${letter}.${body}`;
}

export function decodeSignaling(code: string): SignalingPayload {
  const trimmed = code.trim();
  const parts = trimmed.split('.');
  if (parts.length !== 3) throw new SignalingCodecError('malformed code (expected v1.<o|a>.<body>)');
  const [version, letter, body] = parts;
  if (version !== CODEC_VERSION) throw new SignalingCodecError(`unsupported version ${version}`);
  const kind = LETTER_KIND[letter!];
  if (!kind) throw new SignalingCodecError(`unknown kind letter ${letter}`);

  const json = decompressFromEncodedURIComponent(body!);
  if (!json) throw new SignalingCodecError('decompression returned empty');

  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    throw new SignalingCodecError('decompressed payload is not JSON');
  }

  if (!isSignalingPayload(parsed)) throw new SignalingCodecError('payload missing required fields');
  if (parsed.kind !== kind) throw new SignalingCodecError(`kind mismatch (letter=${letter}, payload=${parsed.kind})`);
  return parsed;
}

function isSignalingPayload(value: unknown): value is SignalingPayload {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    v.v === 1 &&
    (v.kind === 'offer' || v.kind === 'answer') &&
    typeof v.peerId === 'string' &&
    v.peerId.length > 0 &&
    typeof v.sdp === 'string' &&
    v.sdp.length > 0
  );
}
